import React, { useState, useEffect } from 'react';
import { CommonContainer } from '@components';
import {
    Box,
    Tabs,
    Tab,
    Typography,
    Button,
    FormControl,
    InputLabel,
    OutlinedInput,
    Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useSelector, useDispatch } from 'react-redux';
import { apiAddHedgeMargin } from '@store';
import AutoPricingLimits from './AutoPricingLimits';
import EexUserLimits from '../../gas/eex/EexUserLimits';

const implementedConditions = [
    'When volume on any contract is less than',
    'When Months are more than',
    'When Quarter is more than',
    'When seasons are more than',
    'When Calendars are more than'
];

const HedgingMargin = () => {
    const dispatch = useDispatch();
    const { hedgeMargin, isGetHedgeMarginLoading } = useSelector(
        (state) => state.hedgeMargin
    );

    const [value, setValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setValue(newValue);
    };

    return (
        <CommonContainer>
            <Box sx={{ width: '100%' }}>
                <Box
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}
                >
                    <Tabs
                        value={value}
                        onChange={handleTabChange}
                        aria-label="Hedging margin navigation tabs"
                        role="navigation"
                        sx={{
                            background: '#fff',
                            color: 'white',
                            borderRadius: '5px',
                            padding: '5px',
                            margin: '0 0 10px 10px',
                            boxShadow: '0 0 5px rgba(0, 0, 0, 0.1)',
                            minHeight: '45px',
                            '& .MuiTabs-indicator': {
                                display: 'none'
                            },
                            '& .MuiTab-root': {
                                minHeight: '35px',
                                height: '35px',
                                '&.Mui-selected': {
                                    color: '#3AACFE',
                                    background: '#DFF2FF',
                                    borderRadius: '5px',
                                    borderBottom: 'none'
                                }
                            }
                        }}
                    >
                        <Tab label="Auto Pricing Margins" />
                        <Tab label="EEX User Limits" />
                        <Tab label="Hedging Margins" />
                    </Tabs>
                </Box>
                <Box sx={{ padding: '10px' }}>
                    {value === 0 && <AutoPricingLimits />}
                    {value === 1 && <EexUserLimits />}
                    {value === 2 && (
                        <HedgingMarginsContent
                            dispatch={dispatch}
                            hedgeMargin={hedgeMargin}
                            isGetHedgeMarginLoading={isGetHedgeMarginLoading}
                        />
                    )}
                </Box>
            </Box>
        </CommonContainer>
    );
};

const HedgingMarginsContent = ({
    dispatch,
    hedgeMargin,
    isGetHedgeMarginLoading
}) => {
    const [state, setState] = useState(initialState);
    const [statusMessage, setStatusMessage] = useState('');
    const [statusType, setStatusType] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (hedgeMargin?.length > 0) {
            const newState = hedgeMargin
                ?.filter((item) =>
                    implementedConditions?.includes(item?.condition)
                )
                ?.map((item, index) => {
                    return {
                        Condition: item?.condition,
                        MW: item?.mw,
                        Margin: item?.margin
                    };
                });
            setState(newState);
        }
    }, [hedgeMargin]);

    const handleChange = (e, index) => {
        const { name, value } = e.target;
        if (value !== '' && !/^\d*\.?\d{0,3}$/.test(value)) {
            return;
        }
        const list = [...state];
        list[index][name] = value;
        setState(list);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = state?.map((item) => {
            return {
                Condition: item?.Condition,
                MW: parseInt(item?.MW) || 0,
                Margin: parseFloat(item?.Margin) || 0
            };
        });

        setIsSaving(true);
        try {
            const resp = await dispatch(apiAddHedgeMargin(data));
            if (resp?.meta?.requestStatus === 'fulfilled') {
                setStatusMessage('Margins saved successfully!');
                setStatusType('success');
            } else {
                setStatusMessage(
                    resp?.error?.message ||
                        'Unable to save margins. Please try again.'
                );
                setStatusType('error');
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                p: 3,
                maxWidth: '100%',
                width: '100%',
                borderRadius: 2
            }}
        >
            <Box sx={{ mb: 3 }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        background:
                            'linear-gradient(135deg, #0b9cfd 0%, #3DC3C1 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}
                >
                    Hedging Margins
                </Typography>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem'
                }}
            >
                {isGetHedgeMarginLoading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: 5
                        }}
                    >
                        <Typography color="text.secondary">
                            Loading...
                        </Typography>
                    </Box>
                ) : (
                    state?.map((item, index) => (
                        <Box key={index}>
                            <Typography
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '0.85rem',
                                    mb: 0.5
                                }}
                            >
                                {item?.Condition}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '0.75rem'
                                }}
                            >
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel
                                        id={`mwLabel${index}`}
                                        sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                                    >
                                        MW
                                    </InputLabel>
                                    <OutlinedInput
                                        id={`mw${index}`}
                                        label="MW"
                                        name="MW"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.001 }}
                                        onKeyDown={(e) =>
                                            e.key === 'e' && e.preventDefault()
                                        }
                                        onChange={(e) => handleChange(e, index)}
                                        value={item?.MW}
                                    />
                                </FormControl>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel
                                        id={`marginLabel${index}`}
                                        sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                                    >
                                        Margin
                                    </InputLabel>
                                    <OutlinedInput
                                        id={`margin${index}`}
                                        label="Margin"
                                        name="Margin"
                                        type="number"
                                        inputProps={{ min: 0, step: 0.001 }}
                                        onKeyDown={(e) =>
                                            e.key === 'e' && e.preventDefault()
                                        }
                                        onChange={(e) => handleChange(e, index)}
                                        value={item?.Margin}
                                    />
                                </FormControl>
                            </Box>
                        </Box>
                    ))
                )}
            </Box>

            {statusMessage && (
                <Alert
                    severity={statusType}
                    icon={
                        statusType === 'success' ? (
                            <CheckCircleIcon sx={{ color: '#4CAF50' }} />
                        ) : (
                            <ErrorIcon sx={{ color: '#F44336' }} />
                        )
                    }
                    sx={{
                        mt: 2,
                        backgroundColor:
                            statusType === 'success' ? '#E8F5E9' : '#FFEBEE',
                        color: statusType === 'success' ? '#2E7D32' : '#C62828',
                        border: `1px solid ${
                            statusType === 'success' ? '#C8E6C9' : '#FFCDD2'
                        }`
                    }}
                >
                    {statusMessage}
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={isSaving || isGetHedgeMarginLoading}
                    sx={{
                        backgroundColor: '#0b9cfd',
                        color: '#fff',
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        borderRadius: 1,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(11, 156, 253, 0.3)',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 16px rgba(11, 156, 253, 0.4)'
                        },
                        '&:disabled': {
                            background: '#CCCCCC',
                            boxShadow: 'none'
                        },
                        marginLeft: 'auto'
                    }}
                >
                    {isSaving ? 'Saving...' : 'Save Margins'}
                </Button>
            </Box>
        </Box>
    );
};

const initialState = [
    {
        Condition: 'When volume on any contract is less than',
        MW: '',
        Margin: ''
    },
    // {
    //     Condition: "When Spot is more than",
    //     MW: "",
    //     Margin: ""
    // },
    // {
    //     Condition: "For every spot interval",
    //     MW: "",
    //     Margin: ""
    // },
    {
        Condition: 'When Months are more than',
        MW: '',
        Margin: ''
    },
    // {
    //     Condition: "For every Months interval",
    //     MW: "",
    //     Margin: ""
    // },
    {
        Condition: 'When Quarter is more than',
        MW: '',
        Margin: ''
    },
    // {
    //     Condition: "For every Quarter interval",
    //     MW: "",
    //     Margin: ""
    // },
    {
        Condition: 'When seasons are more than',
        MW: '',
        Margin: ''
    },
    // {
    //     Condition: "For every seasons interval",
    //     MW: "",
    //     Margin: ""
    // },
    {
        Condition: 'When Calendars are more than',
        MW: '',
        Margin: ''
    }
    // {
    //     Condition: "For every Calendars interval",
    //     MW: "",
    //     Margin: ""
    // }
];

export default HedgingMargin;
